import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
  getUsers,
  SettlementStatus,
  type SortDirection,
} from "Infrastructure/Api/Api";
import { MS_TIME } from "Utils/TimeUtils";
import { processResponseWithApiProblemDetails } from "Infrastructure/Api/ApiUtils";
import {
  asString,
  asStringArray,
  asBooleanFromString,
  asDateISOString,
  asEnumValue,
} from "Utils/FilterUtils";

export const USERS_LIST_QUERY_KEY = "USERS_LIST";

export type UsersListParams = {
  offset: number;
  limit: number;
  sortBy?: string;
  sortDirection?: SortDirection;
  excludedAnonymous?: boolean;
  verification?: string;
  roleCodes?: Array<string>;
  statuses?: Array<string>;
  firstName?: string;
  lastName?: string;
  companyName?: string;
  login?: string;
  dateCreatedFrom?: string;
  documentExpiration?: string;
  version?: string;
  settlementStatus?: SettlementStatus;
  isAdmin?: boolean;
};

const SORT_COLUMN_MAP: Record<string, string> = {
  documentExpiration: "Party.PartyIdentification.IdentificationValidTo",
};

export const mapUsersSortColumn = (
  sortBy: string | undefined,
): string | undefined =>
  sortBy ? (SORT_COLUMN_MAP[sortBy] ?? sortBy) : undefined;

export const mapUsersFiltersToParams = (
  filters: Record<string, unknown>,
): Partial<UsersListParams> => ({
  verification: asString(filters.CustomVerificationsBadge),
  login: asString(filters.login),
  roleCodes: asStringArray(filters.roleCode),
  statuses: asStringArray(filters.status),
  firstName: asString(filters["party.firstName"]),
  lastName: asString(filters["party.lastName"]),
  companyName: asString(filters["party.companyName"]),
  dateCreatedFrom: asDateISOString(filters.dateCreated),
  documentExpiration: asString(filters.documentExpiration),
  version: asString(filters.version),
  settlementStatus: asEnumValue(
    SettlementStatus,
    filters["party.settlementStatus"],
  ),
  isAdmin: asBooleanFromString(filters.isAdmin),
});

export const usersListKeys = {
  all: [USERS_LIST_QUERY_KEY],
  byParams: (params: UsersListParams) => [...usersListKeys.all, params],
};

export const useUsersListQuery = (params: UsersListParams) =>
  useQuery({
    queryKey: usersListKeys.byParams(params),
    queryFn: processResponseWithApiProblemDetails(() =>
      getUsers(
        params.excludedAnonymous,
        params.verification,
        params.roleCodes,
        params.statuses,
        params.firstName,
        params.lastName,
        params.companyName,
        params.login,
        params.dateCreatedFrom,
        params.documentExpiration,
        params.version,
        params.settlementStatus,
        params.isAdmin,
        params.offset,
        params.limit,
        params.sortBy,
        params.sortDirection,
      ),
    ),
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    staleTime: 5 * MS_TIME.minute,
  });
